import { defineStore } from 'pinia'
import { api } from '@/plugins/api'

// Every user load takes a ticket; a newer load or Close voids the older ones, so a slow
// response can't put an account the admin moved away from back on screen (QA620).
let userTicket = 0
// Same for the "To bill" table: a slow answer for a month or grouping the admin has moved
// away from must not replace the one on screen.
let billingTicket = 0

// ADAS Map Mobile usage and credits (Launchpad API appUsage/*, ADR-0014, ADR-0016). Billing
// Admin (role 28) and Super Admin only on the API side. Every user with ADAS Map Mobile access
// is listed from the moment it's turned on; every change records who made it.
export const appUsageStore = defineStore('appUsage', {
  state: () => ({
    list: { period: '', total: 0, page: 1, pageSize: 50, terms: null, users: [] },
    user: null,
    billing: null,
    loading: {
      list: false,
      user: false,
      saving: false,
      billing: false
    }
  }),
  actions: {
    async getUsers(search, page) {
      this.loading.list = true
      try {
        const data = await api.get('appUsage/users', { search, page, pageSize: 50 })
        this.list = data || { period: '', total: 0, page: 1, pageSize: 50, terms: null, users: [] }
      } finally {
        this.loading.list = false
      }
    },
    // true = shown; false = a newer load or Close came first, so the response was dropped.
    async getUser(launchpadUser) {
      const ticket = ++userTicket
      this.loading.user = true
      try {
        const user = await api.get(`appUsage/users/${launchpadUser}`)
        if (ticket !== userTicket) return false
        this.user = user
        return true
      } catch (error) {
        if (ticket !== userTicket) return false
        throw error
      } finally {
        if (ticket === userTicket) this.loading.user = false
      }
    },
    clearUser() {
      userTicket++
      this.user = null
      this.loading.user = false
    },
    // A save's answer replaces the panel only while the panel still shows that account.
    keepIfShown(launchpadUser, user) {
      if (this.user?.user?.user === launchpadUser) this.user = user
      return user
    },
    // {kind: 'top-up', count} | {kind: 'grant', credits, note} | {kind: 'adjustment', credits, note}
    async addCredits(launchpadUser, body) {
      this.loading.saving = true
      try {
        return this.keepIfShown(launchpadUser, await api.post(`appUsage/users/${launchpadUser}/credits`, body))
      } finally {
        this.loading.saving = false
      }
    },
    // ADR-0016: {billingMode: 'subscription'|'unlimited', note?}
    async setBillingMode(launchpadUser, body) {
      this.loading.saving = true
      try {
        return this.keepIfShown(launchpadUser, await api.post(`appUsage/users/${launchpadUser}/billingMode`, body))
      } finally {
        this.loading.saving = false
      }
    },
    // ADR-0016: {billingState: 'active'|'hold'|'cancelled', note} (note required for hold and cancelled)
    async setBillingState(launchpadUser, body) {
      this.loading.saving = true
      try {
        return this.keepIfShown(launchpadUser, await api.post(`appUsage/users/${launchpadUser}/billingState`, body))
      } finally {
        this.loading.saving = false
      }
    },
    // ADR-0016: {enabled, note} (note required to revoke). Writes the ADAS Map Mobile access flag.
    async setAccess(launchpadUser, body) {
      this.loading.saving = true
      try {
        return this.keepIfShown(launchpadUser, await api.post(`appUsage/users/${launchpadUser}/access`, body))
      } finally {
        this.loading.saving = false
      }
    },
    // ADR-0016: {period, groupBy, asOf, totalUsd, rows}. true = shown; false = superseded.
    async getBilling(period, groupBy, includeTest) {
      const ticket = ++billingTicket
      this.loading.billing = true
      try {
        const data = await api.get('appUsage/billing', billingParams(period, groupBy, includeTest))
        if (ticket !== billingTicket) return false
        this.billing = data
        return true
      } catch (error) {
        if (ticket !== billingTicket) return false
        throw error
      } finally {
        if (ticket === billingTicket) this.loading.billing = false
      }
    },
    // The same table as a CSV file's text.
    async getBillingCsv(period, groupBy, includeTest) {
      return await api.get('appUsage/billing', { ...billingParams(period, groupBy, includeTest), format: 'csv' })
    }
  }
})

function billingParams(period, groupBy, includeTest) {
  const params = { period, groupBy }
  if (includeTest) params.includeTest = 1
  return params
}
